import { previousMonth, type Firm, type UsageModel } from './usageModel';

export type Lens = 'raw' | 'perday' | 'per100' | 'share' | 'indexed';

export const LENS_LABELS: Record<Lens, string> = {
  raw: 'Raw counts',
  perday: 'Per working day',
  per100: 'Per 100 clients',
  share: '% of platform',
  indexed: 'Indexed to 100',
};

export const LENS_UNITS: Record<Lens, string> = {
  raw: '',
  perday: '/day',
  per100: '/100cl',
  share: '%',
  indexed: '',
};

export const LENS_ORDER: readonly Lens[] = ['raw', 'perday', 'per100', 'share', 'indexed'];

/**
 * Per 100 clients on a tiny client base is arithmetic, not information: two clients and thirty
 * actions reads as 1,500 per 100. Ranking panels therefore need a floor. The firm table still
 * shows every firm, with the clients column alongside.
 */
export const MIN_CLIENTS_FOR_PER100 = 50;

/**
 * Working days (Mon-Fri) in a month, stopping at `today` when the month is still running so a
 * part-month is divided by the days actually elapsed rather than the whole month.
 */
function workingDaysIn(monthKey: string, today: Date): number {
  const [year, month] = monthKey.split('-').map(Number);
  const lastDayOfMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();

  const isCurrentMonth = today.getUTCFullYear() === year && today.getUTCMonth() + 1 === month;
  const lastDay = isCurrentMonth ? Math.min(today.getUTCDate(), lastDayOfMonth) : lastDayOfMonth;

  let count = 0;
  for (let day = 1; day <= lastDay; day += 1) {
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    if (weekday !== 0 && weekday !== 6) count += 1;
  }
  return count;
}

type LensContext = {
  model: UsageModel;
  today: Date;
  /** Omitted for platform-level figures, which divide by the whole client base. */
  firm?: Firm;
};

/**
 * Applies a lens to one value for one month. Returns null when the lens cannot be computed - an
 * empty client base, or a month with no platform usage to take a share of - so callers can show a
 * dash rather than a misleading zero or an infinity.
 */
export function applyLens(
  value: number,
  monthKey: string,
  lens: Lens,
  context: LensContext,
): number | null {
  if (lens === 'raw' || lens === 'indexed') return value;

  if (lens === 'perday') {
    const days = workingDaysIn(monthKey, context.today);
    return days > 0 ? value / days : null;
  }

  if (lens === 'per100') {
    const clients = context.firm
      ? (context.firm.clientsAt.get(monthKey) ?? 0)
      : (context.model.cumulativeClients.get(monthKey) ?? 0);
    return clients > 0 ? (value / clients) * 100 : null;
  }

  const platformUsage = context.model.platform.get(monthKey)?.usage ?? 0;
  return platformUsage > 0 ? (value / platformUsage) * 100 : null;
}

/** Rebases a series so its first non-zero point is 100. */
export function indexSeries(values: readonly (number | null)[]): (number | null)[] {
  const base = values.find(value => value !== null && value !== 0) ?? null;
  if (base === null || base === 0) return values.map(() => null);
  return values.map(value => (value === null ? null : (value / base) * 100));
}

type Engagement = 'High' | 'Medium' | 'Low' | 'Clients only' | 'Dormant';

export const ENGAGEMENT_ORDER: Record<Engagement, number> = {
  High: 5,
  Medium: 4,
  Low: 3,
  'Clients only': 2,
  Dormant: 1,
};

export function engagementOf(usage: number, newClients: number): Engagement {
  if (usage >= 50) return 'High';
  if (usage >= 10) return 'Medium';
  if (usage >= 1) return 'Low';
  if (newClients > 0) return 'Clients only';
  return 'Dormant';
}

type Trend = 'Up' | 'Down' | 'Flat' | 'New';

/** +/-10% dead band, so ordinary month-to-month noise does not read as a move. */
const TREND_DEAD_BAND = 0.1;

export function trendOf(firm: Firm, monthKey: string, model: UsageModel): Trend {
  if (firm.first === monthKey) return 'New';

  const current = firm.series.get(monthKey)?.usage ?? 0;
  const prevKey = previousMonth(model, monthKey);
  const previous = prevKey ? (firm.series.get(prevKey)?.usage ?? 0) : 0;

  if (current === 0 && previous === 0) return 'Flat';
  if (previous === 0) return 'Up';

  const change = (current - previous) / previous;
  if (change > TREND_DEAD_BAND) return 'Up';
  if (change < -TREND_DEAD_BAND) return 'Down';
  return 'Flat';
}

export function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

export function formatLensValue(value: number | null, lens: Lens): string {
  if (value === null) return '—';
  if (lens === 'raw') return value.toLocaleString('en-GB');
  if (lens === 'share') return `${value.toFixed(1)}%`;
  if (lens === 'indexed') return value.toFixed(0);
  return value.toLocaleString('en-GB', { maximumFractionDigits: 1 });
}
